/***
*
*   ACCOUNT
*   Index page for account functions
*
**********/

import React, { useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext, Animate, Grid, Card, Icon, Link, Loader, TextInput, Button } from 'components/lib';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export function Account(props){

  const context = useContext(AuthContext);
  const { id: accountId } = useParams();
  const [monitoringLevel, setMonitoringLevel] = useState('');
  const [monitoringLimits, setMonitoringLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Account details state
  const [accountName, setAccountName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [originalName, setOriginalName] = useState('');
  const [originalActive, setOriginalActive] = useState(true);
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const fetchAccountInfo = useCallback(async () => {
    try {
      // Get current account info from organizations list
      const response = await axios.get('/api/user/associatedDataConnections');
      const accounts = response.data.data;
      const currentAccount = accounts.find(acc => acc.id === accountId);
      
      if (currentAccount) {
        setMonitoringLevel(currentAccount.account_type || 'health_score_only');
        setAccountName(currentAccount.org_Name || '');
        setOriginalName(currentAccount.org_Name || '');
        setIsActive(currentAccount.active !== false);
        setOriginalActive(currentAccount.active !== false);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching account info:', error);
      setLoading(false);
    }
  }, [accountId]);

  // Get current account info
  useEffect(() => {
    fetchAccountInfo();
    fetchMonitoringLimits();
  }, [fetchAccountInfo]);

  const fetchMonitoringLimits = async () => {
    try {
      const response = await axios.get('/api/account/monitoring-limits');
      setMonitoringLimits(response.data);
    } catch (error) {
      console.error('Error fetching monitoring limits:', error);
    }
  };

  const toggleMonitoringLevel = async () => {
    setUpdating(true);
    const newLevel = monitoringLevel === 'full_monitoring' ? 'health_score_only' : 'full_monitoring';
    
    try {
      await axios.patch(`/api/account/${accountId}/monitoring-level`, {
        monitoringLevel: newLevel
      });
      
      setMonitoringLevel(newLevel);
      await fetchMonitoringLimits(); // Refresh limits
      
    } catch (error) {
      console.error('Error updating monitoring level:', error);
      alert(error.response?.data?.reason || 'Failed to update monitoring level');
    }
    setUpdating(false);
  };

  const getMonitoringLevelDisplay = () => {
    return monitoringLevel === 'full_monitoring' ? 'Full Monitoring' : 'Health Score Only';
  };

  const getMonitoringDescription = () => {
    if (monitoringLevel === 'full_monitoring') {
      return 'This account has full monitoring enabled with complete data analysis, cleanup recommendations, and all features.';
    } else {
      return 'This account has health score monitoring only. Upgrade to full monitoring for complete data analysis and cleanup recommendations.';
    }
  };

  const canUpgrade = () => {
    if (monitoringLevel === 'full_monitoring') return true; // Can downgrade
    return monitoringLimits?.canUpgradeToFullMonitoring;
  };

  const handleEditDetails = () => {
    setEditingDetails(true);
  };

  const handleCancelEdit = () => {
    setAccountName(originalName);
    setIsActive(originalActive);
    setEditingDetails(false);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    
    try {
      await axios.patch(`/api/account/${accountId}`, {
        name: accountName,
        active: isActive
      });
      
      setOriginalName(accountName);
      setOriginalActive(isActive);
      setEditingDetails(false);
      
      // Refresh the account info to get updated data
      await fetchAccountInfo();
      
    } catch (error) {
      console.error('Error updating account details:', error);
      alert('Failed to update account details');
    }
    setSavingDetails(false);
  };

  const hasChanges = accountName !== originalName || isActive !== originalActive;
  
  return (
    <Animate>
      {/* Profile Section */}
      <h2>Account Settings</h2>

      {/* Account Details Panel */}
      <Card style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <Icon image='user'/>
            <h3>Account Details</h3>
            {loading ? (
              <Loader />
            ) : (
              <div>
                {editingDetails ? (
                  <div style={{ marginTop: '15px' }}>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Account Name</label>
                      <TextInput 
                        value={accountName} 
                        onChange={(name, value) => setAccountName(value)}
                        placeholder="Enter account name"
                        style={{ width: '300px' }}
                      />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input 
                          type="checkbox" 
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontWeight: 'bold' }}>Account is active</span>
                      </label>
                      <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                        Inactive accounts won't sync data or generate reports
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Name:</strong> {accountName || 'No name set'}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Status:</strong>{' '}
                      <span style={{ 
                        color: isActive ? '#4CAF50' : '#f44336',
                        fontWeight: 'bold'
                      }}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {!loading && (
            <div>
              {editingDetails ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button 
                    text="Cancel" 
                    action={handleCancelEdit}
                    color="secondary"
                  />
                  <Button 
                    text={savingDetails ? "Saving..." : "Save"}
                    action={handleSaveDetails}
                    disabled={savingDetails || !hasChanges || !accountName.trim()}
                    color="primary"
                  />
                </div>
              ) : (
                <button 
                  onClick={handleEditDetails}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Edit Details
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Monitoring Level Panel */}
      <Card style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Icon image='analytics'/>
            <h3>Monitoring Level</h3>
            {loading ? (
              <Loader />
            ) : (
              <>
                <div style={{ fontWeight: 'bold', color: monitoringLevel === 'full_monitoring' ? '#2196F3' : '#FF9800' }}>
                  {getMonitoringLevelDisplay()}
                </div>
                <div style={{ marginTop: '10px', color: '#666' }}>
                  {getMonitoringDescription()}
                </div>
                {monitoringLimits && (
                  <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#888' }}>
                    Currently using {monitoringLimits.currentFullyMonitoredCount} fully monitored accounts
                  </div>
                )}
              </>
            )}
          </div>
          
          {!loading && (
            <div>
              <button 
                onClick={toggleMonitoringLevel}
                disabled={updating || !canUpgrade()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: canUpgrade() ? '#2196F3' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: canUpgrade() ? 'pointer' : 'not-allowed'
                }}
              >
                {updating ? 'Updating...' : 
                  monitoringLevel === 'full_monitoring' ? 'Downgrade to Health Score Only' : 'Upgrade to Full Monitoring'
                }
              </button>
              
              {!canUpgrade() && monitoringLimits && (
                <div style={{ marginTop: '10px', fontSize: '0.8em', color: '#f44336' }}>
                  {monitoringLimits.reason}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      { (context.permission?.owner || context.permission?.admin) &&
      <>
      <Grid cols='4'>

          {/* <Card>
            <Icon image='users'/>
            <h2>Users</h2>
            <div>Invite users to your account</div>
            <Link url='/app/account/users'>Manage Users</Link>
          </Card> */}
        
          <Card>
            <Icon image='mail'/>
            <h2>E-mails</h2>
            <div>Get a summary of activity</div>
            <Link url={`/account/${accountId}/emails`}>Manage E-mails</Link>
          </Card>

          <Card style={{ opacity: monitoringLevel === 'health_score_only' ? 0.5 : 1 }}>
            <Icon image='settings'/>
            <h2>API Keys</h2>
            <div>Manage your API keys</div>
            {monitoringLevel === 'health_score_only' ? (
              <div style={{ color: '#999', fontStyle: 'italic' }}>Requires Full Monitoring</div>
            ) : (
              <Link url={`/account/${accountId}/apikeys`}>Manage</Link>
            )}
          </Card>
        
      </Grid>
      </> 
      }
      
    </Animate>
  )
}